// Screensaver camera: the shows the desktop screensavers play (?mode=screensaver).
//
//   ?show=tour               the grand tour (default): the Sun, the planets, the Milky Way
//   ?show=planet&body=earth  one body, cycling slowly between four framings;
//                            &view= picks one view or every view (PLANET_VIEWS)
//   ?show=iss, ?show=cupola  the space station, from outside or from inside the
//                            Cupola (src/screensaver/issshow.js, not this file)
//   ?show=galaxy             the Milky Way from outside, three framings
//   ?show=blackhole          the supermassive black hole and its disc, three
//                            framings (DEEP_SHOWS)
//   ?show=photos             famous space photos as live views
//                            (src/screensaver/photoshow.js, not this file)
//   ?show=galaxies           the galaxy photos in the sky, one at a time
//                            (src/screensaver/galaxyshow.js, not this file)
//
// A scene is a body and a framing. Planet framings are placed relative to the
// Sun (`phase` is the angle between the camera and the sunward direction), so
// the lit side faces the camera whatever today's date puts where; the Sun,
// the galaxy and the black hole use a fixed direction instead.
//
// Close framings stop at the body's detail limit: the distance at which one
// texel of its surface map covers DETAIL_PIXELS_PER_TEXEL screen pixels at the
// centre of the disc. Nearer than that the texture only magnifies into blur,
// so an 8K Earth or Mars comes closer than a 4K Jupiter, and a 1080p screen
// allows closer views than a Retina one.
//
// The camera is held as look point + direction + distance, and a transition
// eases each on its own terms: the look point slides between the targets, the
// direction slerps, and the distance moves on a log scale (a jump from the
// black hole to the Sun covers four decades evenly instead of spending the
// whole move far out and rushing in at the end). Between distant targets the
// camera also backs off and rises above the ecliptic, so the trip reads as a
// flight across the system rather than a cut. The previous scene's orbit keeps
// turning under the blend, so there is no stop at the hand-off either.

import * as THREE from 'three';
import { CURATED_SHOTS } from './shots.js';

const UP = new THREE.Vector3(0, 1, 0);

// Each scene is composed like a film shot: crescents, terminators
// and limbs, the world off centre or over the frame's edge, and a camera move
// while it holds (`to`, see moved()). Ring worlds are framed on their own
// equator (`equator`), so the ring-plane shots hold in any season.
const TOUR = [
  // The Sun's limb across the foot of the frame, closing in.
  { id: 'sun', radii: 1.9, dir: [1, 0, 0], elev: 0.6, shift: [0, -1.07], to: { radii: 1.5 }, hold: 12, orbit: 0.01 },
  // Mercury on its terminator, where the craters throw long shadows.
  { id: 'mercury', radii: 1.6, detail: 1, phase: 1.5, elev: 0.15, shift: [0.45, 0.05], to: { phase: 1.3 }, hold: 10, orbit: 0 },
  // Venus as a crescent.
  { id: 'venus', radii: 3.2, phase: 2.0, elev: 0.12, shift: [-0.3, 0], to: { radii: 2.8 }, hold: 10, orbit: 0.005 },
  // Close over the clouds (Andre's earth-body framing, CURATED_SHOTS).
  { id: 'earth', radii: 0.7556, phase: -1.3548, elev: -0.2192, shift: [-1.4087, 0.5333], aspect: 1.92, hold: 12, orbit: 0.01 },
  // The night side and its cities, the day a thin crescent that widens.
  { id: 'earth', name: 'earth-night', radii: 2.2, phase: 2.55, elev: 0.3, shift: [0.25, -0.15], to: { phase: 2.35 }, hold: 12, orbit: 0 },
  // Over the night side's pole, where the aurora is.
  { id: 'earth', name: 'earth-aurora', winter: true, radii: 1.7, phase: 2.8, elev: 0.95, shift: [-0.3, -0.3], to: { radii: 1.5 }, hold: 12, orbit: 0 },
  // The Moon's terminator.
  { id: 'moon', radii: 1.5, detail: 1, phase: 1.55, elev: 0.1, shift: [0.45, 0.05], hold: 10, orbit: 0.004 },
  // Mars as a crescent.
  { id: 'mars', radii: 2.8, phase: 1.75, elev: 0.15, shift: [0.35, 0], to: { radii: 2.4 }, hold: 10, orbit: 0 },
  // Jupiter's top half filling the frame.
  { id: 'jupiter', name: 'jupiter-moons', dome: 0.6, phase: 0.8, elev: 0.1, hold: 12, orbit: 0.006 },
  // Rising through Saturn's ring plane.
  { id: 'saturn', name: 'saturn-rings', equator: true, radii: 3.4, phase: 0.9, elev: -0.12, shift: [0.3, -0.05], to: { elev: 0.16, radii: 3.0 }, hold: 16, orbit: 0 },
  // Saturn as a crescent, from high over its rings.
  { id: 'saturn', name: 'saturn-crescent', equator: true, radii: 5, phase: 2.2, elev: 0.4, shift: [-0.2, 0], hold: 12, orbit: 0.004 },
  { id: 'uranus', equator: true, radii: 4, phase: 1.3, elev: 0.35, hold: 10, orbit: 0.005 },
  // Neptune as Voyager 2 left it, a crescent.
  { id: 'neptune', radii: 3, phase: 1.6, elev: -0.2, shift: [-0.3, 0.1], to: { radii: 2.6 }, hold: 10, orbit: 0 },
  // Last, the Milky Way from outside (25M units across, src/galaxy.js),
  // tilted across the frame as the live wallpaper's galaxy-sweep has it and
  // closing in. The stellar black hole that followed is gone: from 1.83M
  // units it was too small to see, and the frame showed the galaxy edge-on.
  // ?show=blackhole plays the supermassive one.
  { id: 'milky-way', dist: 3.6e7, elev: 0.32, dir: [1, 0, 0.3], roll: -0.35, shift: [0.22, 0.08], to: { dist: 2.9e7 }, hold: 16, orbit: 0.003 },
];

// The deep-sky shows (?show=galaxy, ?show=blackhole): the grand tour's own
// framing of each, then a few more, held long and turning slowly. Distances
// are the tour's (the galaxy disc is 25M units across, src/galaxy.js).
// The live wallpaper's galaxy (?show=galaxy&wallpaper=1): one held framing,
// tilted and off centre like a desktop picture, the bulge to one side and
// the arms running off the frame. `roll` turns the camera about its view
// axis (radians, so the disc runs diagonally); `shift` moves the target
// [x, y] across the frame in NDC. ?gframe=<name> picks one, for stills.
// All from the far side of the centre (+X), out of the hand-over to the
// inside panorama.
export const WALLPAPER_GALAXY = [
  { id: 'milky-way', name: 'galaxy-sweep', dist: 3.0e7, elev: 0.32, dir: [1, 0, 0.3], roll: -0.35, shift: [0.22, 0.08], hold: Infinity, orbit: 0.002 },
  { id: 'milky-way', name: 'galaxy-near', dist: 2.1e7, elev: 0.26, dir: [0.8, 0, 0.6], roll: -0.45, shift: [0.28, -0.04], hold: Infinity, orbit: 0.0015 },
  { id: 'milky-way', name: 'galaxy-high', dist: 4.2e7, elev: 0.62, dir: [0.5, 0, 1], roll: -0.25, shift: [-0.24, 0], hold: Infinity, orbit: 0.002 },
  { id: 'milky-way', name: 'galaxy-low', dist: 3.4e7, elev: 0.16, dir: [1, 0, 0.2], roll: -0.3, shift: [0.15, 0.12], hold: Infinity, orbit: 0.0015 },
];

const DEEP_SHOWS = {
  galaxy: [
    { id: 'milky-way', name: 'galaxy-above', dist: 6.2e7, elev: 1.1, dir: [0, 0, 1], hold: 60, orbit: 0.008 },
    // About 60 degrees from face-on, like an inclined NGC 1300.
    { id: 'milky-way', name: 'galaxy-tilted', dist: 5.4e7, elev: 0.52, dir: [1, 0, 0.4], hold: 60, orbit: 0.006 },
    // Close and low over the disc, the bulge filling the middle and the arms
    // running off the frame. From the far side of the centre from the Sun:
    // nearer the Sun the galaxy hands over to the inside panorama and fades.
    // The disc lies in the panorama band's plane with its centre toward +X
    // from the Sun (src/galaxy.js), so the far side is +X.
    { id: 'milky-way', name: 'galaxy-close', dist: 2.3e7, elev: 0.5, dir: [0.8, 0, 0.6], hold: 70, orbit: 0.004 },
    // Face-on. (Edge-on stays out until its dust lane is as strong as NGC 891's.)
    { id: 'milky-way', name: 'galaxy-face', dist: 5.6e7, elev: 1.45, dir: [-0.6, 0, 1], hold: 50, orbit: 0.005 },
  ],
  // The supermassive black hole with its accretion disc (src/gargantua.js;
  // main.js enters its system directly for this show and hides the planets
  // and the return wormhole, so only the hole is in frame). r_s is 6,000
  // units and the disc runs from 2.6 to 12 r_s (72k units) in the world's
  // XZ plane, so `elev` is the inclination over the disc. The hero view is
  // nearly edge-on, the far side lensed into the arch over the shadow and
  // under it with the photon ring hugging it; then closer in, then from
  // higher up. The slow orbits keep the inclination and walk the lensed
  // stars round behind.
  blackhole: [
    { id: 'supermassive', name: 'blackhole-galaxy', dist: 230000, elev: 0.05, dir: [0.62, 0, -0.68], fov: 28, hold: 80, orbit: 0.0012 },
    { id: 'supermassive', name: 'blackhole-close', dist: 135000, elev: 0.085, dir: [0.9, 0, -0.3], fov: 44, hold: 70, orbit: -0.0010 },
    { id: 'supermassive', name: 'blackhole-wide', dist: 260000, elev: 0.42, dir: [0.2, 0, -1], fov: 36, hold: 70, orbit: 0.0014 },
  ],
};

// A texel may spread over this many screen pixels before the surface reads
// soft. 1.5 keeps photographic maps crisp under trilinear filtering.
const DETAIL_PIXELS_PER_TEXEL = 1.5;

// The one-planet show's views (?view=), offered by the macOS Options sheet
// (VIEWS in screensaver/macos/SolarSystemView.swift, which greys out the ones
// bodyViews() leaves out for a body):
//   disc   the whole disc, gibbous, so the terminator gives it its roundness
//   dome   the top half of the disc filling the frame, its centre on the
//          frame's bottom edge and the limb arcing across near the top
//   phase  a crescent, the Sun off to one side behind the body
//   limb   low over the lit limb, the horizon arcing across space, the
//          terminator in frame
//   orbit  a slow circle round the body, through its day and its night
//   moon   from behind one of its moons looking back (MOON_VIEWS)
//   cycle  every view the body has, in turn
// One view on its own holds for good and sways about its framing instead of
// circling (SWING), so a crescent stays a crescent. Without ?view= the show
// plays the four framings it has always had (wide, close, limb, high): the
// site's media scripts wait for those scene names.
export const PLANET_VIEWS = ['cycle', 'disc', 'dome', 'phase', 'limb', 'orbit', 'moon', 'left', 'right'];
export const WALLPAPER_DRIFT = 0.6;

// A curated shot travels in a URL as base64url JSON (?shot=), or by name from
// CURATED_SHOTS (src/screensaver/shots.js). Only the framing fields are kept.
const SHOT_FIELDS = ['id', 'name', 'label', 'radii', 'phase', 'dir', 'elev', 'fov', 'roll', 'shift', 'aspect', 'orbit', 'ground'];
export function encodeShot(shot) {
  const clean = {};
  for (const k of SHOT_FIELDS) if (shot[k] != null) clean[k] = shot[k];
  const bytes = new TextEncoder().encode(JSON.stringify(clean));
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
export function decodeShot(value) {
  if (!value) return null;
  const named = CURATED_SHOTS.find((s) => s.name === value);
  if (named) return named;
  try {
    const bin = atob(value.replace(/-/g, '+').replace(/_/g, '/'));
    const shot = JSON.parse(new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0))));
    if (!shot || typeof shot.id !== 'string' || !(shot.radii > 0)) return null;
    const out = {};
    for (const k of SHOT_FIELDS) if (shot[k] != null) out[k] = shot[k];
    if (out.shift && !(Array.isArray(out.shift) && out.shift.length === 2)) delete out.shift;
    return out;
  } catch {
    return null;
  }
}
// Views made for the live wallpaper (wallpaper/): the whole body off to one
// side, leaving the rest of the desktop open for its icons. 'cycle' leaves
// them out, so the screensaver's every-view show is unchanged.
const SIDE_VIEWS = ['left', 'right'];

// The moons a body can be seen from, how far behind the moon the camera sits
// (in the moon's radii, or the moon's detail limit if that is further), and
// the body's reach in its own radii (rings). The moon is picked as the scene
// starts: the one that sees the body most lit. The field of view is set then
// too, so the body and its rings span about 40% of the frame: the Earth is
// 1.9 degrees across from the Moon and needs a long lens, as Apollo 8's
// Earthrise did. The body shows the phase it has from that moon at that
// moment (the Earth's Moon is the ephemeris; the others keep an arbitrary
// phase), so a one-moon body can be a crescent for days.
// Left out for now, from the stills: Mars (Phobos is 11 km across, inside
// the true-scale near plane), Jupiter (the Galilean moons in the foreground
// read as a speckled grey ball), Uranus (its moons are a few hundred km, a
// speck in front of a dark crescent) and Pluto (Charon is not drawn there).
const MOON_VIEWS = {
  earth: { moons: ['moon'], back: 7 },
  saturn: { moons: ['titan', 'rhea', 'dione', 'enceladus'], back: 5, reach: 2.3 },
  neptune: { moons: ['triton'], back: 5 },
};
const MOON_FOV_SPAN = 2.6, MOON_FOV_MIN = 9, MOON_FOV_MAX = 45;
// The moon's limb sits this share of the way from the body's lower edge to
// the bottom of the frame, below the body's centre.
const MOON_LIMB = 0.4;

// Distance for the whole disc, in body radii. Rings need the room.
const DISC_RADII = { saturn: 6.2, uranus: 4.4 };
// The dome view's limb: its top this far up the frame in normalised device
// coordinates (-1 the bottom edge, 1 the top), so a fifth of the frame is
// space above it. The body's centre sits on the bottom edge.
const DOME_TOP = 0.6;
// The side views: the body's centre this far across in NDC (-1 the left
// edge), the body and its rings (`reach` radii) filling this share of the
// frame's height, and the phase that lights the side facing the open space.
const SIDE_X = 0.42, SIDE_FILL = 0.56, SIDE_PHASE = 1.0;
const SIDE_REACH = { saturn: 2.3, uranus: 1.4 };
// A single view's sway about its framing: radians either way, and seconds.
// It also rises and dips (BOB, radians of elevation) and leans in and out
// (DOLLY, a share of the distance) on periods of their own, so the camera
// wanders round the body on every axis while the framing holds.
const SWING = 0.45, SWING_PERIOD = 360;
const BOB = 0.12, BOB_PERIOD = 250;
const DOLLY = 0.05, DOLLY_PERIOD = 300;

export function bodyViews(id) {
  return PLANET_VIEWS.filter((v) => v !== 'cycle' && !SIDE_VIEWS.includes(v)
    && !(v === 'phase' && id === 'sun')
    && !(v === 'moon' && !MOON_VIEWS[id]));
}

// One body's framings, by view. `detail: 1` sits exactly at the detail limit;
// `aim` raises the look point by that many radii so the horizon arcs through
// the frame. Phase angles are measured from the sunward direction: 0.85 rad
// leaves a band of night down one side, 2.05 a thick crescent (2.4 read as
// a black disc with a hairline on the darker bodies).
function planetFramings(id, isSun) {
  const lit = (s) => (isSun ? { ...s, phase: undefined, dir: [1, 0, 0] } : s);
  const disc = DISC_RADII[id] || 3.8;
  const moon = MOON_VIEWS[id];
  return {
    disc: lit({ id, radii: disc, elev: 0.2, phase: 0.85, hold: 35, orbit: 0.012 }),
    dome: lit({ id, dome: DOME_TOP, elev: 0.1, phase: 0.8, hold: 45, orbit: 0.004 }),
    close: lit({ id, radii: 1.3, detail: 1, elev: 0.12, phase: 0.95, hold: 40, orbit: 0.008 }),
    limb: lit({ id, radii: 1.3, detail: 1.05, aim: 0.8, elev: 0.04, phase: 1.1, hold: 40, orbit: 0.006 }),
    high: lit({ id, radii: Math.max(7, disc * 1.6), elev: 0.55, phase: 0.7, hold: 35, orbit: 0.012 }),
    phase: lit({ id, radii: disc, elev: 0.1, phase: 2.05, hold: 40, orbit: 0.008 }),
    orbit: lit({ id, radii: Math.max(3.2, disc * 0.85), elev: 0.25, phase: 0.6, hold: 50, orbit: 0.03 }),
    moon: moon ? { id, ...moon, from: moon.moons[0], fov: MOON_FOV_MAX, hold: 45 } : null,
    left: lit({ id, side: -SIDE_X, fill: SIDE_FILL, reach: SIDE_REACH[id] || 1, elev: 0.12, phase: -SIDE_PHASE, hold: 45, orbit: 0.004 }),
    right: lit({ id, side: SIDE_X, fill: SIDE_FILL, reach: SIDE_REACH[id] || 1, elev: 0.12, phase: SIDE_PHASE, hold: 45, orbit: 0.004 }),
  };
}

function planetScenes(id, view) {
  const f = planetFramings(id, id === 'sun');
  const named = (key, name = key) => ({ ...f[key], name: `${id}-${name}` });
  if (!PLANET_VIEWS.includes(view)) {
    return [named('disc', 'wide'), named('close'), named('limb'), named('high')];
  }
  const views = bodyViews(id);
  // A view the body does not have plays them all, as the Options sheet says.
  if (view === 'cycle' || !(views.includes(view) || SIDE_VIEWS.includes(view))) return views.map((v) => named(v));
  // One view, held for good. An orbit keeps circling; the others sway.
  const one = named(view);
  return [{ ...one, hold: Infinity, ...(view === 'orbit' ? {} : { orbit: 0, swing: SWING }) }];
}

// Distance from the body's centre at which one surface texel covers
// DETAIL_PIXELS_PER_TEXEL pixels: the texel arc 2*pi*R/W against the pixel
// footprint (d - R) * fov / H at the sub-camera point.
export function detailLimitDistance(radius, textureWidth, viewportHeight, fovRad) {
  return radius * (1 + (2 * Math.PI * viewportHeight) / (textureWidth * fovRad * DETAIL_PIXELS_PER_TEXEL));
}

export function readScreensaverOptions(search) {
  const p = new URLSearchParams(search);
  const show = ['planet', 'iss', 'cupola', 'photos', 'galaxy', 'galaxies', 'blackhole', 'shots'].includes(p.get('show')) ? p.get('show') : 'tour';
  // ?hold=0.1 shortens every hold, for checking framings without waiting;
  // ?start=earth-night begins at that scene (the page media captures use both).
  // ?drift=0.35 slows the camera's circling, for short web clips where the
  // screensaver's pace reads as hurried. ?view= picks a planet view
  // (PLANET_VIEWS).
  const hold = Number(p.get('hold')) > 0 ? Number(p.get('hold')) : 1;
  // ?wallpaper=1 (the live wallpaper) gives the galaxy its own framing
  // (WALLPAPER_GALAXY), ?gframe= one of them by name, and circles the camera
  // at WALLPAPER_DRIFT of the screensaver's pace (0.3 until 1.0.8 read as
  // standing still; the full pace swept the close shots' ground across the
  // screen in five seconds).
  const wallpaper = p.get('wallpaper') === '1';
  const drift = Number(p.get('drift')) >= 0 && p.get('drift') !== null ? Number(p.get('drift')) : wallpaper ? WALLPAPER_DRIFT : 1;
  const view = PLANET_VIEWS.includes(p.get('view')) ? p.get('view') : null;
  // ?zoom=1.4 stands every planet view that much further out (the live
  // wallpaper's Zoom menu).
  const zoom = Number(p.get('zoom')) > 0 ? Math.min(4, Math.max(0.5, Number(p.get('zoom')))) : 1;
  // ?shot= one curated shot (decodeShot), held: the curate panel's preview
  // and the live wallpaper's curated views.
  const shot = decodeShot(p.get('shot'));
  return { show, body: (p.get('body') || 'earth').toLowerCase(), view, hold, drift, zoom, wallpaper, gframe: p.get('gframe') || null, start: p.get('start') || null, shot };
}

const smootherstep = (t) => t * t * t * (t * (t * 6 - 15) + 10);

// A scene can move while it holds: `to` gives end values for its numbers
// (radii, phase, elev, fov, roll, aim) or its shift, and the framing eases
// to them over `move` seconds (its hold by default) from the moment the
// flight in lands.
const movedScenes = new WeakMap();
function moved(scene, since) {
  if (!scene.to) return scene;
  let m = movedScenes.get(scene);
  if (!m) movedScenes.set(scene, (m = { ...scene }));
  const k = smootherstep(Math.min(1, Math.max(0, since / (scene.move || scene.hold))));
  for (const key of Object.keys(scene.to)) {
    const a = scene[key], b = scene.to[key];
    m[key] = Array.isArray(b) ? b.map((v, i) => a[i] + (v - a[i]) * k) : a + (b - a) * k;
  }
  return m;
}

// The camera's height over the line from a body through its moon, in moon
// radii, `back` radii behind the moon, that puts the moon's limb `depth`
// radians under the body's centre. Bisection: the limb's depression rises
// with the height (the moon centre drops away and its disc shrinks).
function moonViewLift(back, depth) {
  let lo = 0, hi = back * 4;
  for (let i = 0; i < 40; i++) {
    const lift = (lo + hi) / 2;
    const below = Math.atan2(lift, back) - Math.asin(1 / Math.hypot(back, lift));
    if (below < depth) lo = lift; else hi = lift;
  }
  return (lo + hi) / 2;
}

// ctx: { camera, options, resolve(id) -> Object3D | null, radiusOf(obj), sun(),
//        detailDistance(obj, fovDeg) -> world units, lookTarget() }
export function createScreensaverTour(ctx) {
  const { camera } = ctx;
  // Scenes without a field of view of their own keep the camera's.
  const baseFov = camera.fov;
  let fromFov = baseFov;
  let scenes = null;
  let index = -1;
  let start = 0;
  let trans = 0;
  let prev = null;
  const look = new THREE.Vector3();
  // `offset` is the look point relative to the previous body, so a move that
  // starts mid-blend leaves from where the camera was actually looking.
  const from = { look: new THREE.Vector3(), offset: new THREE.Vector3(), dir: new THREE.Vector3(), up: new THREE.Vector3(0, 1, 0), dist: 1 };

  const tmpT = new THREE.Vector3();
  const tmpS = new THREE.Vector3();
  const tmpDir = new THREE.Vector3();
  const tmpFromDir = new THREE.Vector3();
  const tmpBase = new THREE.Vector3();
  const tmpUp = new THREE.Vector3();
  const upQ = new THREE.Quaternion();
  const tmpQ = new THREE.Quaternion();
  const fullQ = new THREE.Quaternion();
  const IDENTITY = new THREE.Quaternion();
  const invFrame = new THREE.Quaternion();

  function build() {
    if (ctx.options.shot && ctx.resolve(ctx.options.shot.id)) {
      return [{ ...ctx.options.shot, name: ctx.options.shot.name || 'shot', hold: Infinity }];
    }
    const show = ctx.options.show;
    // ?show=shots: every curated shot in turn, two minutes each.
    if (show === 'shots') {
      const shots = CURATED_SHOTS.filter((sc) => ctx.resolve(sc.id)).map((sc) => ({ ...sc, hold: 120 }));
      if (shots.length) return shots;
    }
    const wallpaperGalaxy = show === 'galaxy' && ctx.options.wallpaper
      ? [WALLPAPER_GALAXY.find((s) => s.name === ctx.options.gframe) || WALLPAPER_GALAXY[0]]
      : null;
    const list = show === 'planet' ? planetScenes(ctx.options.body, ctx.options.view)
      : wallpaperGalaxy || DEEP_SHOWS[show] || TOUR;
    const ready = list.filter((s) => ctx.resolve(s.id) && (!s.moons || s.moons.some((m) => ctx.resolve(m))));
    // An unknown or unloaded body falls back to the grand tour.
    return ready.length ? ready : TOUR.filter((s) => ctx.resolve(s.id));
  }

  // Where the scene wants the camera `since` seconds in: writes the target's
  // position to outLook and the unit view direction (target -> camera) to
  // outDir, returns the distance.
  // From behind the moon looking back at the body: the camera `back` moon
  // radii out along the line from the body through the moon and raised over
  // it until the moon's limb sits under the body, which stays whole in
  // frame; the side it rises on is ecliptic north, swaying about that line.
  const tmpU = new THREE.Vector3();
  const tmpW = new THREE.Vector3();
  // As a moon view starts: the moon that sees the body most lit (the Sun
  // behind the camera lights the moon's face too), and the lens for it.
  function chooseMoon(scene) {
    const target = ctx.resolve(scene.id);
    target.getWorldPosition(tmpT);
    ctx.sun().getWorldPosition(tmpS).sub(tmpT).normalize();
    let best = -2;
    for (const id of scene.moons) {
      const moon = ctx.resolve(id);
      if (!moon) continue;
      const lit = moon.getWorldPosition(tmpU).sub(tmpT).normalize().dot(tmpS);
      if (lit > best) { best = lit; scene.from = id; }
    }
    const moon = ctx.resolve(scene.from);
    const D = moon.getWorldPosition(tmpU).distanceTo(tmpT);
    const span = 2 * THREE.MathUtils.radToDeg(Math.atan(((scene.reach || 1) * ctx.radiusOf(target)) / D));
    scene.fov = THREE.MathUtils.clamp(span * MOON_FOV_SPAN, MOON_FOV_MIN, MOON_FOV_MAX);
  }

  function moonPose(scene, since, outLook, outDir) {
    const target = ctx.resolve(scene.id), moon = ctx.resolve(scene.from);
    target.getWorldPosition(outLook);
    moon.getWorldPosition(tmpS);
    tmpU.subVectors(tmpS, outLook);
    const D = tmpU.length();
    tmpU.divideScalar(D);
    tmpW.copy(UP).addScaledVector(tmpU, -UP.dot(tmpU));
    if (tmpW.lengthSq() < 1e-9) tmpW.set(1, 0, 0).addScaledVector(tmpU, -tmpU.x);
    tmpW.normalize().applyAxisAngle(tmpU, SWING * 0.5 * Math.sin((2 * Math.PI * since * (ctx.options.drift ?? 1)) / SWING_PERIOD));
    const rMoon = ctx.radiusOf(moon);
    const back = Math.max(scene.back, ctx.detailDistance(moon, scene.fov) / rMoon);
    const body = Math.asin(Math.min(1, ctx.radiusOf(target) / D));
    const half = THREE.MathUtils.degToRad(scene.fov) / 2;
    const lift = moonViewLift(back, body + MOON_LIMB * Math.max(0, half - body));
    tmpS.addScaledVector(tmpU, back * rMoon).addScaledVector(tmpW, lift * rMoon);
    outDir.subVectors(tmpS, outLook);
    const dist = outDir.length();
    outDir.divideScalar(dist);
    return dist;
  }

  // A target with a frame of its own (the galaxy: ctx.frame, from
  // galaxy.framingQuaternion) has its framings written in that frame's
  // axes: the pose is worked out there and turned into the world, and the
  // camera's up turns with it.
  // A ground-locked shot (scene.ground) takes the world's own spinning frame
  // (ctx.frame(id, scene)): its `dir`, `elev` and `shift` are in the world's
  // axes, so it stays over one place while the day goes round. An equatorial
  // shot (scene.equator) takes the world's tilt without its spin: `elev` is
  // over its equator and its rings, and `phase` still from the Sun, so a
  // ring-plane shot stays one in any season.
  const frameOf = (scene) => (ctx.frame && scene && !scene.from ? ctx.frame(scene.id, scene) : null);
  function upOf(scene, out) {
    const q = frameOf(scene);
    return q ? out.copy(UP).applyQuaternion(q) : out.copy(UP);
  }

  // A winter shot (the aurora's) looks down on whichever pole is turned away
  // from the Sun: `elev` and the vertical `shift` change sign while the
  // north pole leans sunward, so the polar night stays in frame all year.
  const winterScenes = new WeakMap();
  function winterSide(scene, at) {
    const tilt = ctx.frame && ctx.frame(scene.id, { equator: true });
    if (!tilt) return scene;
    ctx.sun().getWorldPosition(tmpS).sub(at);
    if (tmpU.copy(UP).applyQuaternion(tilt).dot(tmpS) <= 0) return scene;
    let w = winterScenes.get(scene);
    if (!w) winterScenes.set(scene, (w = {}));
    Object.assign(w, scene, { elev: -scene.elev, shift: scene.shift && [scene.shift[0], -scene.shift[1]] });
    return w;
  }

  // Where the scene wants the camera, worked out in the target's frame (the
  // world's axes unless frameOf gives one) and then turned into the world.
  // `held` is the seconds since the flight in landed (moved()).
  function pose(scene, since, outLook, outDir, held = 0) {
    if (scene.from) return moonPose(scene, since, outLook, outDir);
    const target = ctx.resolve(scene.id);
    target.getWorldPosition(outLook);
    tmpBase.copy(outLook);
    const frame = frameOf(scene);
    let framed = moved(scene, held);
    if (scene.winter) framed = winterSide(framed, outLook);
    let dist = framedPose(framed, since, target, frame, outLook, outDir);
    // The dome keeps its limb where it was framed, so it does not lean.
    if (framed.swing && framed.dome == null) {
      dist *= 1 + DOLLY * Math.sin((2 * Math.PI * (ctx.options.drift ?? 1) * since) / DOLLY_PERIOD + 2.3);
    }
    if (frame) {
      outDir.applyQuaternion(frame);
      outLook.sub(tmpBase).applyQuaternion(frame).add(tmpBase);
    }
    return dist;
  }

  function framedPose(scene, since, target, frame, outLook, outDir) {
    if (scene.dir) {
      outDir.set(scene.dir[0], 0, scene.dir[2]);
    } else {
      // The Sun's direction in the frame's axes, so `phase` and `elev`
      // stay sun-relative in a tilted frame (scene.equator).
      ctx.sun().getWorldPosition(tmpS);
      outDir.subVectors(tmpS, outLook);
      if (frame) outDir.applyQuaternion(invFrame.copy(frame).invert());
      outDir.setY(0);
      if (outDir.lengthSq() < 1e-9) outDir.set(1, 0, 0);
      outDir.normalize().applyAxisAngle(UP, scene.phase || 0);
    }
    if (scene.side != null) {
      // Moving the look point sideways (below) also turns the body towards
      // the camera by atan(side * aspect * tan(V/2)), which would eat that
      // much of the phase: turn the view on by the same angle so the body
      // shows its full phase.
      const half = THREE.MathUtils.degToRad(scene.fov || baseFov) / 2;
      outDir.applyAxisAngle(UP, Math.sign(scene.side) * Math.atan(Math.abs(scene.side) * (camera.aspect || 16 / 9) * Math.tan(half)));
    }
    const paced = (ctx.options.drift ?? 1) * since;
    const swing = scene.swing ? scene.swing * Math.sin((2 * Math.PI * paced) / SWING_PERIOD) : 0;
    const elev = scene.swing ? scene.elev + BOB * Math.sin((2 * Math.PI * paced) / BOB_PERIOD + 1.1) : scene.elev;
    outDir.normalize().applyAxisAngle(UP, (scene.orbit || 0) * paced + swing);
    outDir.multiplyScalar(Math.cos(elev)).addScaledVector(UP, Math.sin(elev)).normalize();
    if (scene.dist) {
      if (scene.shift) shiftLook(scene, scene.dist, outLook, outDir);
      return scene.dist;
    }
    const radius = ctx.radiusOf(target);
    const zoom = ctx.options.zoom || 1;
    if (scene.side != null) {
      // The body and its rings span `fill` of the frame's height: angular
      // radius a with tan(a) = fill * tan(V/2). The look point moves along
      // the screen's right so the centre lands `side` across in NDC: an
      // offset s at distance d shows the centre at -s / (d * aspect *
      // tan(V/2)).
      const half = THREE.MathUtils.degToRad(scene.fov || baseFov) / 2;
      const a = Math.atan(scene.fill * Math.tan(half));
      const dist = (zoom * (scene.reach || 1) * radius) / Math.sin(a);
      tmpS.crossVectors(UP, outDir).normalize();
      outLook.addScaledVector(tmpS, -scene.side * dist * (camera.aspect || 16 / 9) * Math.tan(half));
      return dist;
    }
    if (scene.dome != null) {
      // The disc's angular radius a that puts its top at `dome` in NDC with
      // its centre half a field of view under the axis: tan(a - V/2) =
      // dome * tan(V/2). The camera stands R / sin(a) from the centre, and
      // the look point and camera both rise by sin(V/2) of that along the
      // screen's up, so the centre lands on the bottom edge.
      const half = THREE.MathUtils.degToRad(scene.fov || baseFov) / 2;
      const a = Math.min(1.45, half + Math.atan(scene.dome * Math.tan(half)));
      const far = (zoom * radius) / Math.sin(a);
      tmpS.copy(UP).addScaledVector(outDir, -UP.dot(outDir)).normalize();
      outLook.addScaledVector(tmpS, far * Math.sin(half));
      return far * Math.cos(half);
    }
    if (scene.aim) {
      // Raise the look point along the screen's up so the limb crosses the frame.
      tmpS.copy(UP).addScaledVector(outDir, -UP.dot(outDir)).normalize();
      outLook.addScaledVector(tmpS, scene.aim * radius);
    }
    const near = scene.detail ? ctx.detailDistance(target, scene.fov || baseFov) * scene.detail : 0;
    const dist = zoom * Math.max(scene.radii * radius, near);
    if (scene.shift) shiftLook(scene, dist, outLook, outDir);
    return dist;
  }

  // Move the target across the frame: an offset s along the screen's right
  // at distance d shows it at -s / (d * aspect * tan(V/2)) in NDC, and
  // likewise up. src/ui/curate.js inverts this. A curated shot carries the
  // aspect it was framed at, so the camera stands where it was put and a
  // screen of another shape only crops the sides (with the screen's own
  // aspect, a shot framed wide put the camera inside the planet on 16:9).
  function shiftLook(scene, dist, outLook, outDir) {
    const half = THREE.MathUtils.degToRad(scene.fov || baseFov) / 2;
    const k = dist * Math.tan(half);
    tmpS.crossVectors(UP, outDir).normalize();
    outLook.addScaledVector(tmpS, -scene.shift[0] * k * (scene.aspect || camera.aspect || 16 / 9));
    tmpS.copy(UP).addScaledVector(outDir, -UP.dot(outDir)).normalize();
    outLook.addScaledVector(tmpS, -scene.shift[1] * k);
  }

  function enter(i, elapsed) {
    // Snapshot the camera as look point + direction + distance.
    from.look.copy(look);
    from.dir.subVectors(camera.position, look);
    from.dist = Math.max(1e-3, from.dir.length());
    from.dir.divideScalar(from.dist);
    from.up.copy(camera.up);
    fromFov = camera.fov;
    prev = index >= 0 ? scenes[index] : null;
    const prevTarget = prev && ctx.resolve(prev.id);
    if (prevTarget) from.offset.subVectors(look, prevTarget.getWorldPosition(tmpS));
    index = i;
    start = elapsed;
    const scene = scenes[index];
    if (scene.moons) chooseMoon(scene);
    const d1 = pose(scene, 0, tmpT, tmpDir);
    const travel = tmpT.distanceTo(from.look);
    const span = Math.max(1, travel / ((from.dist + d1) / 2));
    // At true scale most of a long hop is empty space; cap it so the trip
    // reads as travel without leaving an empty sky on screen for long.
    trans = THREE.MathUtils.clamp(4.5 + 1.2 * Math.log10(span), 4.5, 9);
    if (document.body.dataset.ssScene !== (scene.name || scene.id)) {
      // screensaver/record-assets.mjs follows this to time a full cycle.
      document.body.dataset.ssScene = scene.name || scene.id;
    }
  }

  function tick(elapsed) {
    if (!scenes) {
      scenes = build();
      if (!scenes.length) return;
      // First frame: take whatever the camera is looking at as the origin.
      if (ctx.lookTarget) look.copy(ctx.lookTarget());
      const first = ctx.options.start ? scenes.findIndex((sc) => (sc.name || sc.id) === ctx.options.start) : 0;
      enter(Math.max(0, first), elapsed);
    }
    const scene = scenes[index];
    const since = elapsed - start;
    if (since >= trans + scene.hold * (ctx.options.hold || 1)) {
      enter((index + 1) % scenes.length, elapsed);
      return tick(elapsed);
    }

    const held = Math.max(0, since - trans);
    const dist1 = pose(scene, since, tmpT, tmpDir, held);
    const now = moved(scene, held);
    const t = Math.min(1, since / trans);
    // The field of view eases over the whole move, on a log scale like the
    // distance, so a long lens closes in as evenly as it opens out.
    const fov = now.fov || baseFov;
    const fovNow = t >= 1 ? fov : Math.exp(THREE.MathUtils.lerp(Math.log(fromFov), Math.log(fov), smootherstep(t)));
    if (Math.abs(camera.fov - fovNow) > 1e-6) {
      camera.fov = fovNow;
      camera.updateProjectionMatrix();
    }
    if (t >= 1) {
      // For stills (scripts/screensaver-views.mjs): the move has landed.
      if (document.body.dataset.ssHeld !== document.body.dataset.ssScene) document.body.dataset.ssHeld = document.body.dataset.ssScene;
      look.copy(tmpT);
      camera.position.copy(tmpT).addScaledVector(tmpDir, dist1);
      upOf(scene, camera.up);
      camera.lookAt(look);
      if (now.roll) camera.rotateZ(now.roll);
      return;
    }

    const e = smootherstep(t);
    // The previous framing keeps drifting under the blend; the look point it
    // leaves from follows its body, which may itself be moving.
    tmpFromDir.copy(from.dir);
    let fromLook = from.look;
    if (prev) {
      tmpFromDir.applyAxisAngle(upOf(prev, tmpS), (prev.orbit || 0) * (ctx.options.drift ?? 1) * since);
      const prevTarget = ctx.resolve(prev.id);
      if (prevTarget) fromLook = prevTarget.getWorldPosition(tmpS).add(from.offset);
    }

    // The view turns onto the destination in the first 45% of the move and
    // then flies in on it, so no frame looks at the empty space between.
    look.lerpVectors(fromLook, tmpT, smootherstep(Math.min(1, t / 0.45)));
    const travel = fromLook.distanceTo(tmpT);
    const reach = Math.min(1, travel / (from.dist + dist1));
    const arc = Math.sin(Math.PI * e);

    // Direction: slerp, then lift above the ecliptic on long hops.
    fullQ.setFromUnitVectors(tmpFromDir, tmpDir);
    tmpQ.slerpQuaternions(IDENTITY, fullQ, e);
    const dir = tmpFromDir.applyQuaternion(tmpQ).addScaledVector(UP, 0.8 * reach * arc).normalize();

    // Distance: log-space ease, widened into an arc so both ends of a long
    // hop are in frame at the midpoint.
    const logDist = Math.exp(THREE.MathUtils.lerp(Math.log(from.dist), Math.log(dist1), e));
    const out = 0.35 * travel * arc;
    const dist = Math.sqrt(logDist * logDist + out * out);

    camera.position.copy(look).addScaledVector(dir, dist);
    upOf(scene, tmpUp);
    upQ.setFromUnitVectors(from.up, tmpUp);
    tmpQ.slerpQuaternions(IDENTITY, upQ, e);
    camera.up.copy(from.up).applyQuaternion(tmpQ);
    camera.lookAt(look);
    // A rolled framing (the wallpaper's galaxy) turns in over the move.
    const prevRoll = prev ? moved(prev, Infinity).roll : 0;
    const roll = THREE.MathUtils.lerp(prevRoll || 0, now.roll || 0, e);
    if (roll) camera.rotateZ(roll);
  }

  return { tick };
}
